// Presentation rules for the coach report web page (/report/:code).
//
// Every value the page renders is already formatted server-side — see
// backend/app/services/report_web.py. What lives here is the handful of
// *display* decisions that have branches worth testing.
//
// Spec: docs/superpowers/specs/2026-08-05-coach-app-and-list-sharing-design.md §4
// Design: HANDOFF.md §8.1

const EM_DASH = '—';

const isBlank = (v) => v == null || v === '' || v === EM_DASH;

/**
 * The card's footer pill. Commitment beats offers, always — a committed athlete
 * with four offers reads as committed, not as a target.
 * @returns {{text: string, tone: 'committed'|'offers'}|null}
 */
export function recruitingPill(card) {
  if (card.committed) {
    const college = card.committed_college_name;
    return {
      text: isBlank(college) ? 'Committed' : `Committed · ${college}`,
      tone: 'committed',
    };
  }
  const n = card.offers_count || 0;
  if (n > 0) return { text: `${n} Offer${n === 1 ? '' : 's'}`, tone: 'offers' };
  return null;
}

/** Position chip, or null when there is no position to show. */
export function positionChip(card) {
  return isBlank(card.position) ? null : String(card.position);
}

/** Class-of chip, or null when there is no graduation year to show. */
export function classChip(card) {
  return isBlank(card.grad_year) ? null : `Class of ${card.grad_year}`;
}

/**
 * Truncation is never silent: a coach who shares a 63-athlete list must not
 * believe all 63 went out. Returns null when nothing was cut.
 */
export function truncationNotice(report) {
  if (!report.truncated_from) return null;
  return `Showing ${report.athlete_count} of ${report.truncated_from} athletes.`;
}

/**
 * An empty report is not an error and not a 404 — the code stays valid because
 * it may already be printed on a QR or sitting in someone's texts. It happens
 * when the coach still has the list but has removed everyone from it.
 *
 * The link is still live and will start working again the moment the coach adds
 * someone. Rendering that as a bare empty table reads as a bug, so it gets its
 * own state with a message explaining the link is still valid.
 * @returns {'ok'|'unavailable'}
 */
export function reportState(web) {
  return (web.rows || []).length > 0 ? 'ok' : 'unavailable';
}
